import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { OptionEditor } from './option-editor';
import {
  isTempId,
  newTempId,
  useDeleteModifierGroup,
  useDeleteModifierOption,
  useModifierGroups,
  useSaveModifierGroup,
  type ModifierGroup,
  type ModifierKind,
  type ModifierOption,
} from './modifiers.api';

/**
 * Requirements, sides and add-ons.
 *
 * One page, three nouns. They were three files that differed by a word and by
 * which columns an option showed, and the three drifted: the same fix had to be
 * made three times, and twice it was not.
 *
 * Options are edited in memory and written with the group, so reordering four
 * of them and adding a fifth is one save rather than five.
 */

const EMPTY_GROUP: ModifierGroup = {
  id: '',
  internalName: '',
  name: '',
  minRequired: 0,
  maxRequired: 1,
  amountOptionsCountForMinMax: false,
  items: [],
};

function emptyOption(): ModifierOption {
  return {
    id: newTempId(),
    name: '',
    description: '',
    imageUrl: '',
    ordering: 0,
    isAvailable: true,
    allowVariableAmount: false,
    brand: '',
    itemId: null,
    price: 0,
    isAlcoholicBeverage: false,
  };
}

export function ModifiersPage({ kind }: { kind: ModifierKind }) {
  const { t } = useTranslation();

  const list = useModifierGroups(kind);
  const save = useSaveModifierGroup(kind);
  const removeGroup = useDeleteModifierGroup(kind);
  const removeOption = useDeleteModifierOption(kind);

  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<ModifierGroup | null>(null);
  const [editingOption, setEditingOption] = useState<ModifierOption | null>(
    null
  );

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (list.data ?? []).filter(
      (group) =>
        !needle ||
        `${group.name ?? ''} ${group.internalName ?? ''}`
          .toLowerCase()
          .includes(needle)
    );
  }, [list.data, query]);

  // Rows already on screen being refreshed — the reload button, or the
  // invalidation after a save.
  const busy = list.isFetching && !list.isPending;

  const isEdit = !!draft?.id && !isTempId(draft.id);
  const options = draft?.items ?? [];
  const failed = () => toast.error(t('modifiers.actionFailed'));

  const setGroup = (patch: Partial<ModifierGroup>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const setOptions = (items: ModifierOption[]) => setGroup({ items });

  /** Up and down buttons rather than drag: they work with a thumb and a keyboard. */
  function move(index: number, by: number) {
    const next = [...options];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOptions(next);
  }

  /**
   * Drop an option. One that was never saved just leaves the array; a saved one
   * is removed on the API first, because the group save posts what is left and
   * would otherwise silently keep it.
   */
  function dropOption(option: ModifierOption) {
    const without = options.filter((item) => item.id !== option.id);
    if (isTempId(option.id)) {
      setOptions(without);
      return;
    }
    removeOption.mutate(option.id, {
      onSuccess: () => setOptions(without),
      onError: failed,
    });
  }

  const titleKey = {
    requirement: 'modifiers.requirementsTitle',
    side: 'modifiers.sidesTitle',
    addon: 'modifiers.addonsTitle',
  }[kind];

  const subtitleKey = {
    requirement: 'modifiers.requirementsSubtitle',
    side: 'modifiers.sidesSubtitle',
    addon: 'modifiers.addonsSubtitle',
  }[kind];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(titleKey)}
        description={t(subtitleKey)}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('modifiers.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => setDraft({ ...EMPTY_GROUP })}>
              <Plus />
              <span className="hidden sm:inline">
                {t('modifiers.createNew')}
              </span>
            </Button>
          </>
        }
      />

      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('modifiers.searchPlaceholder')}
          aria-label={t('modifiers.searchPlaceholder')}
          className="h-10 pl-9"
        />
      </div>

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('modifiers.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="m-3 h-10" />
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('modifiers.noGroups')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        // `relative` on a wrapper outside the clipped list, so the spinner
        // can stick to the visible part of a long one.
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <div
            aria-busy={busy}
            className={cn(
              'divide-y overflow-hidden rounded-xl border',
              busy && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((group) => (
              <GroupRow
                key={group.id}
                kind={kind}
                group={group}
                onEdit={() => setDraft(group)}
              />
            ))}
          </div>
        </div>
      )}

      {/* -------------------------------------------------- group editor */}
      <Sheet
        open={draft !== null}
        onOpenChange={(next) => {
          if (!next) setDraft(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
        >
          <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
            <SheetTitle>
              {isEdit ? t('modifiers.editGroup') : t('modifiers.newGroup')}
            </SheetTitle>
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    disabled={save.isPending || removeGroup.isPending}
                  >
                    <Trash2 className="size-3.5" />
                    {t('modifiers.delete')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('modifiers.delete')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('modifiers.deleteGroupPrompt')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        removeGroup.mutate(draft!.id, {
                          onSuccess: () => {
                            toast.success(t('modifiers.deleted'));
                            setDraft(null);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </SheetHeader>

          {draft && (
            <div className="space-y-4 p-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  {t('modifiers.internalName')}
                </Label>
                <Input
                  value={draft.internalName ?? ''}
                  onChange={(e) => setGroup({ internalName: e.target.value })}
                />
                <p className="text-muted-foreground text-xs">
                  {t('modifiers.internalNameHint')}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  {t('modifiers.name')}
                </Label>
                <Input
                  value={draft.name ?? ''}
                  onChange={(e) => setGroup({ name: e.target.value })}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">
                    {t('modifiers.minimum')}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={draft.minRequired ?? 0}
                    onChange={(e) =>
                      setGroup({ minRequired: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">
                    {t('modifiers.maximum')}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={draft.maxRequired ?? 0}
                    onChange={(e) =>
                      setGroup({ maxRequired: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div className="flex items-start justify-between gap-4 py-1">
                <div className="min-w-0">
                  <Label className="text-sm font-normal">
                    {t('modifiers.amountCountsForMinMax')}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t('modifiers.amountCountsForMinMaxHint')}
                  </p>
                </div>
                <Switch
                  checked={draft.amountOptionsCountForMinMax}
                  onCheckedChange={(amountOptionsCountForMinMax) =>
                    setGroup({ amountOptionsCountForMinMax })
                  }
                />
              </div>

              {/* ------------------------------------------- options */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-xs font-semibold">
                    {t('modifiers.options')}
                    <span className="text-muted-foreground ml-1.5 font-normal tabular-nums">
                      {options.length}
                    </span>
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingOption(emptyOption())}
                  >
                    <Plus className="size-3.5" />
                    {t('modifiers.addOption')}
                  </Button>
                </div>

                {options.length === 0 ? (
                  <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
                    {t('modifiers.noOptions')}
                  </p>
                ) : (
                  <div className="divide-y rounded-xl border">
                    {options.map((option, index) => (
                      <div
                        key={option.id}
                        className="flex items-center gap-2 p-2.5"
                      >
                        {/* Up/down rather than drag: it works with a thumb
                            and with a keyboard, and the order is what the
                            storefront shows. */}
                        <div className="flex shrink-0 flex-col">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="size-5"
                            aria-label={t('modifiers.moveUp')}
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                          >
                            <ChevronUp className="size-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="size-5"
                            aria-label={t('modifiers.moveDown')}
                            disabled={index === options.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            <ChevronDown className="size-3.5" />
                          </Button>
                        </div>

                        {option.imageUrl ? (
                          <img
                            src={option.imageUrl}
                            alt=""
                            className="bg-muted size-9 shrink-0 rounded object-contain"
                          />
                        ) : (
                          <span className="bg-muted size-9 shrink-0 rounded" />
                        )}

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{option.name}</p>
                          <p className="text-muted-foreground truncate text-xs">
                            {[
                              option.brand,
                              option.description,
                              kind === 'addon' && (option.price ?? 0) > 0
                                ? formatCurrency(option.price)
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>

                        {kind !== 'side' && !option.isAvailable && (
                          <Badge variant="secondary">
                            {t('modifiers.unavailable')}
                          </Badge>
                        )}

                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('modifiers.editOption')}
                          onClick={() => setEditingOption(option)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive"
                          aria-label={t('modifiers.removeOption')}
                          disabled={removeOption.isPending}
                          onClick={() => dropOption(option)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={save.isPending}
                  onClick={() => setDraft(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={save.isPending || !draft.name?.trim()}
                  onClick={() =>
                    save.mutate(draft, {
                      onSuccess: () => {
                        toast.success(t('modifiers.saved'));
                        setDraft(null);
                      },
                      onError: failed,
                    })
                  }
                >
                  {save.isPending && <Loader2 className="animate-spin" />}
                  {isEdit ? t('common.save') : t('modifiers.create')}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <OptionEditor
        kind={kind}
        option={editingOption}
        open={editingOption !== null}
        onOpenChange={(next) => {
          if (!next) setEditingOption(null);
        }}
        onSave={(option) => {
          const exists = options.some((item) => item.id === option.id);
          setOptions(
            exists
              ? options.map((item) => (item.id === option.id ? option : item))
              : [...options, option]
          );
        }}
      />
    </div>
  );
}

/** One group in the list, opening to the options it holds. */
function GroupRow({
  kind,
  group,
  onEdit,
}: {
  kind: ModifierKind;
  group: ModifierGroup;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const options = group.items ?? [];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-3 p-3">
        <CollapsibleTrigger
          className="hover:bg-accent/40 -m-1 flex min-w-0 flex-1 items-center gap-2 rounded-lg p-1 text-left transition-colors"
          disabled={options.length === 0}
        >
          <ChevronDown
            className={cn(
              'text-muted-foreground size-4 shrink-0 transition-transform',
              open && 'rotate-180',
              options.length === 0 && 'invisible'
            )}
          />
          <div className="min-w-0">
            <p className="truncate font-medium">{group.name || '—'}</p>
            <p className="text-muted-foreground truncate text-xs">
              {group.internalName}
            </p>
          </div>
        </CollapsibleTrigger>

        <div className="text-muted-foreground shrink-0 text-right text-xs">
          <p className="tabular-nums">
            {t('modifiers.pickRange', {
              min: group.minRequired ?? 0,
              max: group.maxRequired ?? 0,
            })}
          </p>
          <p className="tabular-nums">
            {t('modifiers.nOptions', { count: options.length })}
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="size-3.5" />
          {t('modifiers.edit')}
        </Button>
      </div>

      <CollapsibleContent>
        <div className="bg-muted/40 divide-y border-t">
          {options.map((option) => (
            <div key={option.id} className="flex items-center gap-3 p-2.5 pl-9">
              {option.imageUrl ? (
                <img
                  src={option.imageUrl}
                  alt=""
                  className="bg-background size-9 shrink-0 rounded object-contain"
                />
              ) : (
                <span className="bg-background size-9 shrink-0 rounded" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{option.name}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {[option.brand, option.description].filter(Boolean).join(' · ')}
                </p>
              </div>
              {kind === 'addon' && (
                <p className="shrink-0 text-sm tabular-nums">
                  {formatCurrency(option.price)}
                </p>
              )}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function RequirementsPage() {
  return <ModifiersPage kind="requirement" />;
}

export function SidesPage() {
  return <ModifiersPage kind="side" />;
}

export function AddonsPage() {
  return <ModifiersPage kind="addon" />;
}
